import {
  Checkbox,
  Text
} from '@fluentui/react';
import * as React from 'react';

import { EgressOptionType } from '../../../../Common/CommonModels/EgressOptionTypeEnum';
import { SubscriptionNode } from '../../../../Common/ConsumerModels/SubcriptionNode';
import * as Styled from '../../../../Common/Styles/attributeSubscriptions.styled';

type AddAttributeSubscriptionsFormProps = {
  formattedSubscriptionNodes: SubscriptionNode[];
  setFormattedSubscriptionNodes(subscriptionNodes: SubscriptionNode[]): void;
  filteredSubscriptionNodes: SubscriptionNode[];
  setFilteredSubscriptionNodes(subscriptionNodes: SubscriptionNode[]): void;
  isAttributeSubscriptionLoading: boolean;
  searchQuery: string;
  checkedAttributeCount: number;
  setCheckedAttributeCount(val: number): void;
  filteredTenantIndex: number;
  filteredCdmIndex: number;
  egressType: EgressOptionType;
};

export function AttributeSubscriptions(props: AddAttributeSubscriptionsFormProps): React.ReactElement {
  const {
    formattedSubscriptionNodes,
    setFormattedSubscriptionNodes,
    filteredSubscriptionNodes,
    setFilteredSubscriptionNodes,
    isAttributeSubscriptionLoading,
    searchQuery,
    checkedAttributeCount,
    setCheckedAttributeCount,
    filteredTenantIndex,
    filteredCdmIndex,
    egressType
  } = props;
  const areCheckboxesDisabled = egressType === EgressOptionType.Kusto;
  const checkBoxStyles = {
    text: {
      padding: '0px 0px 0px 4px',
    },
  };

  const getFormattedIndexesFromAttributeNode = (pathFromRoot: string) => {
    const formattedIndexesList: number[] = pathFromRoot.split('###') as unknown as number[];
    const formattedTenantIndex: number = formattedIndexesList[0];
    const formattedCdmIndex: number = formattedIndexesList[1];
    const formattedAttributeIndex: number = formattedIndexesList[2];
    return { formattedTenantIndex, formattedCdmIndex, formattedAttributeIndex };
  };

  const getAttributeKeyLabel = (attributeName: string, cdmNode: SubscriptionNode): string => {
    if (cdmNode.primaryKey && cdmNode.primaryKey === attributeName) {
      return " [Primary key]";
    }

    if (Array.isArray(cdmNode.alternateKeys) &&
        cdmNode.alternateKeys.includes(attributeName)) {
      return " [Alternate key]";
    }

    if (Array.isArray(cdmNode.compositeKey) &&
        cdmNode.compositeKey.includes(attributeName)) {
      return " [Composite key]";
    }
    return "";
  };

  const isAllAttributesSelectedForCdm = (
    subscriptionNodes: SubscriptionNode[],
    tenantIndex: number,
    cdmIndex: number
  ): boolean => {
    const attributeList = subscriptionNodes[tenantIndex].children[cdmIndex].children;
    for (const attribute of attributeList) {
      if (!attribute.isCheckedAttribute) {
        return false;
      }
    }
    return true;
  };

  const onChangeSelectSingleCheckBox = (
    checked: boolean,
    attributeNode: SubscriptionNode,
    filteredTenantIndex: number,
    filteredCdmIndex: number,
    filteredAttributeIndex: number
  ): void => {

    // Set isCheckedAttribute for filteredSubscriptionNodes tree
    const newFilteredSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(filteredSubscriptionNodes) // deep copy
    );
    newFilteredSubscriptionNodes[filteredTenantIndex]
      .children[filteredCdmIndex]
      .children[filteredAttributeIndex]
      .isCheckedAttribute = checked;
    newFilteredSubscriptionNodes[filteredTenantIndex]
      .children[filteredCdmIndex]
      .isAllSelected = isAllAttributesSelectedForCdm(newFilteredSubscriptionNodes, filteredTenantIndex, filteredCdmIndex);
    setFilteredSubscriptionNodes(newFilteredSubscriptionNodes);

    // Set isCheckedAttribute for formattedSubscriptionNodes tree
    const newFormattedSubscriptionNodes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedSubscriptionNodes) // deep copy
    );
    const { formattedTenantIndex, formattedCdmIndex, formattedAttributeIndex } = 
      getFormattedIndexesFromAttributeNode(attributeNode.pathFromRoot);
    newFormattedSubscriptionNodes[formattedTenantIndex]
      .children[formattedCdmIndex]
      .children[formattedAttributeIndex]
      .isCheckedAttribute = checked;
    newFormattedSubscriptionNodes[formattedTenantIndex]
      .children[formattedCdmIndex]
      .isAllSelected = isAllAttributesSelectedForCdm(newFormattedSubscriptionNodes, formattedTenantIndex, formattedCdmIndex);
    setFormattedSubscriptionNodes(newFormattedSubscriptionNodes);

    // Adjust the attribute count for error validation
    setCheckedAttributeCount(checked ? checkedAttributeCount + 1 : checkedAttributeCount - 1);
  };

  const renderAttributeCheckbox = (
    attributeNode: SubscriptionNode,
    cdmNode: SubscriptionNode,
    filteredTenantIndex: number,
    filteredCdmIndex: number,
    filteredAttributeIndex: number
  ): JSX.Element => {

    const keyLabel = getAttributeKeyLabel(attributeNode.name, cdmNode);
    return (
      <Checkbox
        key={attributeNode.pathFromRoot}
        label={attributeNode.name}
        onRenderLabel={() => (
          <span>
            {attributeNode.name}
            {attributeNode.keyLabel && <Styled.KeyTagText>{attributeNode.keyLabel}</Styled.KeyTagText>}
            {attributeNode.isApprovedAttribute && <Styled.ApprovedTag>[Approved]</Styled.ApprovedTag>}
          </span>
        )}
        id={attributeNode.pathFromRoot}
        ariaLabel={`${attributeNode.name} Check Box`}
        checked={attributeNode.isCheckedAttribute}
        onChange={(ev, checked) =>
          onChangeSelectSingleCheckBox(
            checked,
            attributeNode,
            filteredTenantIndex,
            filteredCdmIndex,
            filteredAttributeIndex
          )
        }
        styles={checkBoxStyles}
        disabled={areCheckboxesDisabled || attributeNode.isApprovedAttribute}
      />
    );
  };
  const cdmNode = filteredSubscriptionNodes[filteredTenantIndex].children[filteredCdmIndex];
  return (
    <Styled.AttributeSubscriptionsContainer>
      {filteredSubscriptionNodes[filteredTenantIndex]
        .children[filteredCdmIndex]
        .children
        .map((attributeNode, filteredAttributeIndex) =>
          renderAttributeCheckbox(attributeNode, cdmNode, filteredTenantIndex, filteredCdmIndex, filteredAttributeIndex)
        )
      }
    </Styled.AttributeSubscriptionsContainer>
  );
}
  